import React, { useEffect, useRef, useState } from "react";
import { Card, Row, Col } from "reactstrap";
import Select from "react-select/lib/Select";
import { FiInfo } from "react-icons/fi";
import { toastContainer } from "../../../../utils/toast";
import {
  TRIVIA_SHARE_PRIVACY_OPTIONS,
  TRIVIA_USER_IDENTIFICATION_OPTIONS,
} from "../../data";
import {
  SHARE_NOTE_LABEL,
  SHARE_NOTE_PUBLIC_LINK,
  SHARE_LINK_SHARING_PERMISSIONS,
  SHARE_LINK_USER_MUST_PROVIDE,
  SHARE_LINK_SETTINGS_SAVED,
  SHARE_LINK_SETTINGS_SAVE_FAILED,
} from "./shareCopy";

// TBV-951: the Copy Link modal's sharing options — Sharing Permissions, plus
// User Must Provide for "Anyone with this link" — in the Share page's exact
// card layout. Shared by Trivvys and Library items; each passes its own
// `saveSettings(updateData) => Promise`. Every change saves at once
// (optimistic, reverted on failure), and the controls stay disabled while a
// save is in flight so saves never overlap.
const LinkSharingOptions = ({
  initialPrivacy,
  initialUserIdentification,
  disabled = false,
  planInvalid = false,
  notice,
  publicNote = SHARE_NOTE_PUBLIC_LINK,
  saveSettings,
  onSaved,
}) => {
  const [privacy, setPrivacy] = useState(initialPrivacy);
  const [userIdentification, setUserIdentification] = useState(initialUserIdentification);
  const [isSaving, setIsSaving] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const saveSetting = (updateData, setValue, value, previousValue) => {
    setValue(value);
    setIsSaving(true);
    saveSettings(updateData)
      .then((result) => {
        toastContainer(SHARE_LINK_SETTINGS_SAVED, "success");
        if (onSaved) {
          onSaved(updateData, result);
        }
      })
      .catch((error) => {
        console.log(error);
        if (isMountedRef.current) {
          setValue(previousValue);
        }
        toastContainer(SHARE_LINK_SETTINGS_SAVE_FAILED, "error");
      })
      .then(() => {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      });
  };

  const handlePrivacyChange = (option) => {
    if (!option || option.value === privacy) {
      return;
    }
    saveSetting({ privacy: option.value }, setPrivacy, option.value, privacy);
  };

  const handleUserIdentificationChange = (option) => {
    if (!option || option.value === userIdentification) {
      return;
    }
    // Stored as an array, like the Share page saves it.
    saveSetting(
      { userIdentificationOptions: [option.value] },
      setUserIdentification,
      option.value,
      userIdentification
    );
  };

  return (
    <div className="mb-3">
      {notice && (
        <div className="share_card_note mb-3">
          <span className="share_note_icon">
            <FiInfo />
          </span>
          <span>{notice}</span>
        </div>
      )}

      <Card className="pt-2 px-4 sharepage_top_card">
        <div className="d-flex flex-column">
          <Row className="mt-3 share-quiz-privacy">
            <Col md={6} sm={12} lg={6} xs={12}>
              <div className="row text-left my-3">
                <div className="col-12 text-left">
                  <span className="share_label_caps">{SHARE_LINK_SHARING_PERMISSIONS}</span>
                </div>
                <div className="col-12 enterprise-settings-value">
                  <Select
                    className="pt-0"
                    value={privacy}
                    onChange={handlePrivacyChange}
                    options={TRIVIA_SHARE_PRIVACY_OPTIONS}
                    placeholder="Select Privacy"
                    clearable={false}
                    disabled={disabled || isSaving}
                  />
                </div>
              </div>
            </Col>

            <Col md={6} sm={12} lg={6} xs={12}>
              {privacy === "PUBLIC" && (
                <div className="row text-left my-3">
                  <div className="col-12 text-left">
                    <span className="share_label_caps">{SHARE_LINK_USER_MUST_PROVIDE}</span>
                  </div>
                  <div className="col-12 enterprise-settings-value">
                    <Select
                      className="pt-0"
                      value={userIdentification}
                      onChange={handleUserIdentificationChange}
                      options={TRIVIA_USER_IDENTIFICATION_OPTIONS}
                      placeholder="Select User Identification"
                      clearable={false}
                      disabled={planInvalid || disabled || isSaving}
                    />
                    {planInvalid && (
                      <small className="text-muted">
                        Upgrade your plan to Premium or above to enable this feature
                      </small>
                    )}
                  </div>
                </div>
              )}
            </Col>
          </Row>
          {privacy === "PUBLIC" && (
            <div className="share_card_note">
              <span className="share_note_icon">
                <FiInfo />
              </span>
              <span>
                <b>{SHARE_NOTE_LABEL}</b> {publicNote}
              </span>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default LinkSharingOptions;
